import { Link } from "react-router-dom";
import { Instagram, Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { BUSINESS, NAV_LINKS, SERVICES } from "@/lib/site";

export default function Footer() {
  return (
    <footer className="border-t border-white/10 bg-panel/40" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-8 md:grid-cols-[1.4fr_1fr_1fr_1.2fr] lg:py-20">
        <div>
          <div className="flex items-center gap-3">
            <img src="/images/logo.png" alt={`${BUSINESS.name} logo`} className="h-12 w-12 rounded-lg object-contain" width="48" height="48" />
            <span className="font-display text-sm font-bold uppercase leading-tight tracking-[0.18em] text-white">
              Skies The Limit
              <span className="block text-[10px] font-medium tracking-[0.3em] text-gold">Creations LLC</span>
            </span>
          </div>
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-mist">
            A family-owned custom creation shop — 3D printing, laser engraving, embroidery, DTF printing, sublimation, custom apparel and personalized gifts.
          </p>
          <a
            href={BUSINESS.instagram.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram-link"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-medium text-mist transition-colors duration-200 hover:border-gold/50 hover:text-white"
          >
            <Instagram className="h-4 w-4 text-gold" /> {BUSINESS.instagram.label}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        </div>

        <nav aria-label="Footer — explore">
          <p className="eyebrow mb-5">Explore</p>
          <ul className="space-y-3">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} data-testid={`footer-${l.to === "/" ? "home" : l.to.slice(1)}-link`} className="text-sm text-mist transition-colors duration-200 hover:text-white">
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/quote" data-testid="footer-quote-link" className="text-sm font-medium text-ember transition-colors duration-200 hover:text-ember-glow">
                Request a Quote
              </Link>
            </li>
          </ul>
        </nav>

        <nav aria-label="Footer — services">
          <p className="eyebrow mb-5">Services</p>
          <ul className="space-y-3">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link to={`/services#${s.slug}`} data-testid={`footer-service-${s.slug}-link`} className="text-sm text-mist transition-colors duration-200 hover:text-white">
                  {s.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <p className="eyebrow mb-5">Get in touch</p>
          <ul className="space-y-4 text-sm">
            <li>
              <a href={`mailto:${BUSINESS.email}`} data-testid="footer-email-link" className="flex items-start gap-3 text-mist transition-colors duration-200 hover:text-white">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                <span className="break-all">{BUSINESS.email}</span>
              </a>
            </li>
            <li>
              <a href={BUSINESS.phoneHref} data-testid="footer-phone-link" className="flex items-center gap-3 text-mist transition-colors duration-200 hover:text-white">
                <Phone className="h-4 w-4 shrink-0 text-gold" /> {BUSINESS.phoneDisplay}
              </a>
            </li>
            <li className="flex items-start gap-3 text-mist">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
              {BUSINESS.address ? BUSINESS.address : <span className="rounded border border-dashed border-white/20 px-2 py-0.5 text-xs">[Add your address or service area]</span>}
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-5 py-6 text-xs text-mist sm:flex-row sm:px-8">
          <p>© {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.</p>
          <p className="font-mono uppercase tracking-[0.2em]">Family-owned &amp; operated</p>
        </div>
      </div>
    </footer>
  );
}
